/**
 * Iter 44 — Provider-side Messages inbox.
 *
 * The first surface a provider sees their admin notes. Split-pane:
 *   • Left: list of messages with unread dot, subject, sender, context
 *           chips, and timestamp.
 *   • Right: selected message body + full context card + quick toggle
 *           to mark read/unread.
 *
 * MVP — no reply action yet, no threading. Unread count mirrors the
 * sidebar badge (polled every 30s via the same endpoint).
 */
import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
// FP3-035F Issue 2 — Don't auto-mark messages read or allow toggle
// when admin is shadowing the provider workspace.
// FP3-035F correction (Issue 1) — Hard-redirect admin shadowing
// the provider away from the private inbox.
import { useReadOnlyAsProvider } from "@/components/layout/ReadOnlyProviderBanner";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Envelope, EnvelopeOpen, UserCircle, Tag, ArrowSquareOut, Check,
} from "@phosphor-icons/react";
import api from "@/lib/api";

const KIND_LABELS = {
  progress_goal: "Goal progress",
  progress_student: "Student progress",
  coverage_violation: "Coverage / minutes",
  compliance: "Compliance",
  scheduling: "Scheduling",
  service: "Service delivery",
  general: "General",
};

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMin = Math.max(0, Math.floor((now - then) / 60000));
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const h = Math.floor(diffMin / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function ProviderMessages() {
  const readOnly = useReadOnlyAsProvider();
  const { user } = useAuth();
  const [provider, setProvider] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    api.getCurrentProvider().then((r) => setProvider(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!provider?.id) return;
    setLoading(true);
    // FP3-069 follow-up #13 — Merge admin notes (`/messages`) AND
    // provider-to-provider DMs (`/provider-messages`) into a single
    // inbox. Before this change, supervisor→assistant DMs sent from
    // the AssistantsCard Message composer landed in
    // `provider_messages` but Max's Messages page only queried
    // `messages` (admin notes), so Pete's DMs were invisible to Max.
    Promise.all([
      api.listMessages({ to_provider_id: provider.id }).catch(() => ({ data: [] })),
      api.listProviderMessages().catch(() => ({ data: [] })),
    ]).then(([adminR, dmR]) => {
      const adminRows = (adminR.data || []).map((m) => ({
        ...m,
        // The admin-notes endpoint already returns this shape, but
        // we stamp the channel explicitly so the rest of the page
        // can branch on it without sniffing field presence.
        channel: "admin_note",
      }));
      const dmRows = (dmR.data || []).map((m) => ({
        // Normalize provider-DM shape onto the admin-note shape so
        // the existing list + detail UI keeps working. DMs don't
        // carry a subject; we synthesize one from the sender + an
        // optional context label.
        id: m.id,
        channel: "provider_dm",
        // Mark inbound vs outbound so the Sarah-sent-this view can
        // still surface in her own sent thread (Pete sees his own
        // outbound DM in the same list). We treat outbound as read.
        direction:
          m.to_user_id === user?.id || m.to_provider_id === provider.id
            ? "in"
            : "out",
        subject:
          m.context_label
          || `DM from ${`${m.from_first_name || ""} ${m.from_last_name || ""}`.trim() || "supervisor"}`,
        body: m.body,
        from_user_id: m.from_user_id,
        from_user_name:
          `${m.from_first_name || ""} ${m.from_last_name || ""}`.trim()
          || "Supervisor",
        to_provider_id: m.to_provider_id || provider.id,
        // Provider-DM `read_at` is "" for unread and an ISO string
        // when read; coerce empty → null so the existing `!m.read_at`
        // unread check works.
        read_at: m.read_at || null,
        created_at: m.created_at,
        context: m.context_label || m.context_type
          ? {
              kind: m.context_type || "general",
              ref_label: m.context_label || "",
            }
          : null,
      }));
      // Inbound-only DMs in the list (sender's outbound copy lives in
      // a separate sent-history surface, not this inbox). This matches
      // the admin-note behavior: the admin doesn't see their own sent
      // notes in the recipient's inbox.
      const dmIn = dmRows.filter((m) => m.direction === "in");
      const merged = [...adminRows, ...dmIn].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      setMessages(merged);
      if (merged.length && !selectedId) setSelectedId(merged[0].id);
    }).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider?.id, user?.id]);

  const selected = useMemo(
    () => messages.find((m) => m.id === selectedId) || null,
    [messages, selectedId],
  );

  // Mark the selected message read on open if it isn't already.
  // FP3-035F Issue 2 — Skip the auto-mark when admin is shadowing
  // the provider so the unread state stays unchanged for the actual
  // provider's inbox.
  // FP3-069 follow-up #13 — Dispatch by channel: admin notes use
  // /messages/{id}/read, provider DMs use /provider-messages/{id}/read.
  useEffect(() => {
    if (!selected || selected.read_at) return;
    if (readOnly.active) return;
    let cancelled = false;
    const markFn =
      selected.channel === "provider_dm"
        ? api.markProviderMessageRead
        : api.markMessageRead;
    markFn(selected.id).then((r) => {
      if (cancelled) return;
      setMessages((prev) =>
        prev.map((m) =>
          m.id === selected.id
            ? { ...m, read_at: r.data?.read_at || new Date().toISOString() }
            : m
        )
      );
      // Nudge sidebar to refresh its badge.
      window.dispatchEvent(new Event("messages:refresh-badge"));
    });
    return () => { cancelled = true; };
  }, [selected?.id, selected?.read_at, selected?.channel, readOnly.active]);

  const toggleUnread = async () => {
    if (!selected) return;
    if (readOnly.active) return;
    try {
      // FP3-069 follow-up #13 — Dispatch by channel.
      const isDm = selected.channel === "provider_dm";
      const r = selected.read_at
        ? await (isDm ? api.markProviderMessageUnread(selected.id) : api.markMessageUnread(selected.id))
        : await (isDm ? api.markProviderMessageRead(selected.id) : api.markMessageRead(selected.id));
      const nextRead = r.data?.read_at;
      setMessages((prev) =>
        prev.map((m) =>
          m.id === selected.id
            ? { ...m, read_at: nextRead || null }
            : m
        )
      );
      window.dispatchEvent(new Event("messages:refresh-badge"));
    } catch (e) {
      toast.error(`Update failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const unreadCount = messages.filter((m) => !m.read_at).length;

  // FP3-035F correction (Issue 1) — Admin shadowing the provider
  // must never see the private inbox. Redirect direct nav (manual
  // URL, bookmark, or stale sidebar entry) back to the provider's
  // read-only Today page with the shadow flag intact. Placed after
  // all hooks above so the hook order stays stable.
  //
  // FP3-069 follow-up #12 — Scope the redirect to ADMIN shadow only.
  // A supervising primary in supervision-shadow mode (e.g. Sarah
  // clicking Messages from the supervision sidebar while shadowing
  // Taylor) is opening HER OWN inbox to DM the assistant, not
  // peeking at someone else's inbox. The supervision shadow should
  // pass through to the Messages page normally — the
  // useReadOnlyAsProvider().mode field disambiguates.
  if (readOnly.active && readOnly.mode === "admin") {
    return (
      <Navigate
        to={`/?as_provider=${readOnly.providerId}`}
        replace
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="provider-messages">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-xl font-semibold text-[#1F2937] heading-font">Messages</h1>
          <p className="text-[11px] text-[#6B7280] mt-0.5">
            Notes from your admin — tied to the student, goal, or schedule they were reviewing.
          </p>
        </div>
        <div className="text-[11px] text-[#6B7280]">
          <span data-testid="messages-total-count">{messages.length}</span> total ·
          <span className="ml-1 font-semibold text-[#1E3F33]" data-testid="messages-unread-count">
            {unreadCount}
          </span>{" "}
          unread
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-[360px_1fr] gap-3">
          <Skeleton className="h-72" />
          <Skeleton className="h-72" />
        </div>
      ) : messages.length === 0 ? (
        <Card className="border-[#E5E3DB]">
          <CardContent className="py-10 text-center" data-testid="messages-empty">
            <Envelope size={32} weight="duotone" className="mx-auto text-[#9B9890] mb-2" />
            <p className="text-sm text-[#6B7280]">
              No messages yet. Admin notes and supervisor DMs will land here when you receive them.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-[360px_1fr] gap-3">
          {/* List column */}
          <Card className="border-[#E5E3DB] overflow-hidden" data-testid="messages-list">
            <ul className="divide-y divide-[#ECEAE4] max-h-[70vh] overflow-y-auto">
              {messages.map((m) => {
                const unread = !m.read_at;
                const active = m.id === selectedId;
                return (
                  <li key={m.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(m.id)}
                      data-testid={`messages-list-item-${m.id}`}
                      className={`w-full text-left px-3 py-2.5 transition-colors flex flex-col gap-0.5 ${
                        active
                          ? "bg-[#E8F0EB]"
                          : unread
                            ? "bg-[#FDFBF6] hover:bg-[#F9F7F2]"
                            : "hover:bg-[#F8F7F3]"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {unread ? (
                          <span
                            className="w-1.5 h-1.5 rounded-full bg-[#1E3F33] shrink-0"
                            aria-label="Unread"
                          />
                        ) : (
                          <span className="w-1.5 h-1.5 rounded-full bg-transparent shrink-0" />
                        )}
                        <span
                          className={`text-[13px] truncate ${
                            unread ? "font-semibold text-[#1F2937]" : "text-[#3F4756]"
                          }`}
                        >
                          {m.subject}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-[10.5px] text-[#6B7280] pl-3.5">
                        <UserCircle size={11} weight="duotone" />
                        <span className="truncate">{m.from_user_name || "Admin"}</span>
                        {/* FP3-069 follow-up #13 — Channel chip so
                            assistants can distinguish a supervisor
                            DM from an admin note at a glance. */}
                        {m.channel === "provider_dm" && (
                          <span
                            data-testid={`messages-list-item-${m.id}-channel`}
                            className="inline-flex items-center text-[9px] uppercase tracking-wider font-semibold px-1.5 py-[1px] rounded-full bg-[#EEF1F6] text-[#3F4756]"
                          >
                            DM
                          </span>
                        )}
                        <span className="ml-auto shrink-0">{timeAgo(m.created_at)}</span>
                      </div>
                      {m.context?.student_name && (
                        <div className="pl-3.5 mt-0.5">
                          <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-[#1E3F33]/10 text-[#1E3F33]">
                            {m.context.student_name}
                          </span>
                        </div>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </Card>

          {/* Detail column */}
          <Card className="border-[#E5E3DB]" data-testid="messages-detail">
            {selected ? (
              <CardContent className="p-5 space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="text-base font-semibold text-[#1F2937] heading-font">
                      {selected.subject}
                    </h2>
                    <div className="flex items-center gap-2 text-[11px] text-[#6B7280] mt-1">
                      <UserCircle size={12} weight="duotone" />
                      <span>{selected.from_user_name || "Admin"}</span>
                      <span>·</span>
                      <span>{new Date(selected.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                  {!readOnly.active && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={toggleUnread}
                      data-testid="messages-toggle-unread"
                      className="shrink-0 h-8 text-[11px]"
                    >
                      {selected.read_at ? (
                        <>
                          <Envelope size={12} weight="duotone" className="mr-1" />
                          Mark unread
                        </>
                      ) : (
                        <>
                          <Check size={12} weight="bold" className="mr-1" />
                          Mark read
                        </>
                      )}
                    </Button>
                  )}
                </div>

                {/* Context card */}
                {selected.context && (
                  <div
                    className="rounded-lg border border-[#E8E4D8] bg-[#FDFBF6] p-3 space-y-1.5"
                    data-testid="messages-context-card"
                  >
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">
                      <Tag size={11} weight="duotone" /> Context
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {selected.context.kind && (
                        <span className="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#3F4756]/10 text-[#3F4756]">
                          {KIND_LABELS[selected.context.kind] || selected.context.kind}
                        </span>
                      )}
                      {selected.context.student_name && (
                        <span className="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#1E3F33]/10 text-[#1E3F33]">
                          {selected.context.student_name}
                          {selected.context.student_readable_id && (
                            <span className="ml-1 opacity-70 font-mono text-[10px]">
                              · {selected.context.student_readable_id}
                            </span>
                          )}
                        </span>
                      )}
                      {selected.context.goal_label && (
                        <span className="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#4A6B8C]/10 text-[#4A6B8C]">
                          Goal: {selected.context.goal_label}
                        </span>
                      )}
                      {selected.context.violation_label && (
                        <span className="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#9C6B2E]/10 text-[#9C6B2E]">
                          {selected.context.violation_label}
                        </span>
                      )}
                      {selected.context.ref_label && !selected.context.violation_label && (
                        <span className="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#9B9890]/15 text-[#6B7280]">
                          {selected.context.ref_label}
                        </span>
                      )}
                    </div>
                    {selected.context.ref_url && (
                      <a
                        href={selected.context.ref_url}
                        className="inline-flex items-center gap-1 text-[11px] font-medium text-[#1E3F33] hover:underline"
                        data-testid="messages-context-jump"
                      >
                        <ArrowSquareOut size={11} weight="duotone" />
                        Open referenced view
                      </a>
                    )}
                  </div>
                )}

                {/* Body */}
                <div
                  className="text-[13px] leading-relaxed text-[#1F2937] whitespace-pre-wrap"
                  data-testid="messages-body"
                >
                  {selected.body}
                </div>
              </CardContent>
            ) : (
              <CardContent className="py-10 text-center">
                <EnvelopeOpen size={28} weight="duotone" className="mx-auto text-[#9B9890] mb-2" />
                <p className="text-[12px] text-[#6B7280]">Select a message to view its contents.</p>
              </CardContent>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
